import React from 'react';
import PropTypes from 'prop-types';
import {
  ERROR_TEXT_EMPTY_FORM_NAME,
  ERROR_TEXT_NO_DEGREE_OFFERING,
  ERROR_TEXT_NO_PROGRAM_OF_STUDY,
  ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
  ERROR_TEXT_NO_GDPR_SETTING,
  ERROR_DISCLAIMER_REQUIRED,
} from 'langs/en/errors';
import {
  buildAvailablePrograms,
  buildAvailableProgramsOfStudy,
} from 'utils/component.utils';
import {
  DISCLAIMER_LOCATION_TYPES,
  FORM_STYLE,
  GDPR_SETTING,
  CREDENTIAL_TYPES,
  LEGACY_GDPR_SETTINGS,
  GDPR_SETTING_DISPLAY_LABELS,
} from 'utils/constants';
import SelectAttribute from 'components/SelectAttribute';
import { useFetchSfFieldData } from 'containers/FormBuilder/hooks/useFetchSfFieldData';
import InputContainer from '../InputContainer';
import CheckBoxAttribute from '../Fields/CheckBoxAttribute';
import TextAttribute from '../Fields/TextAttribute';
import TextAreaAttribute from '../Fields/TextAreaAttribute';

/**
 * Maps PASS_CONSENT_FOR_ALL_USERS to the effective GDPR setting for UI rendering based on credential type
 */
function getEffectiveGdprSetting(gdprSetting, availableCredentialTypes) {
  if (gdprSetting !== GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS) {
    return gdprSetting;
  }

  const credentialType = availableCredentialTypes?.[0]?.value;

  if (credentialType === CREDENTIAL_TYPES.DEGREE) {
    return GDPR_SETTING.GEO_IMPLIED;
  }

  if (credentialType === CREDENTIAL_TYPES.SHORT_COURSE) {
    return GDPR_SETTING.ALWAYS;
  }

  return gdprSetting;
}

const FormDetails = (props) => {
  const {
    availablePrograms = [],
    availableProgramsOfStudy = [],
    availableCredentialTypes = [],
    form,
    fieldValidityChanged,
    onConsentStyleChange,
    onFieldValueChange,
    onUIStateChange,
    supportsDegreeOffering,
    updateDefaultDegreeOffering,
    updateProgramOfStudy,
    uiState,
  } = props;

  const isImpliedConsent = Boolean(form.formMetadata?.formAttributesConfig?.impliedConsent);
  const effectiveGdprSetting = getEffectiveGdprSetting(form.syntheticContentEntities?.gdprSetting, availableCredentialTypes);
  const isGeoImplied = effectiveGdprSetting === GDPR_SETTING.GEO_IMPLIED;
  const aboveFooterDisclaimer = form.syntheticContentEntities?.disclaimers?.[DISCLAIMER_LOCATION_TYPES.body];
  const belowFooterDisclaimer = form.syntheticContentEntities?.disclaimers?.[DISCLAIMER_LOCATION_TYPES.footer];
  const aboveFooterDisclaimerKeyPath = ['syntheticContentEntities', 'disclaimers', DISCLAIMER_LOCATION_TYPES.body];
  const belowFooterDisclaimerKeyPath = ['syntheticContentEntities', 'disclaimers', DISCLAIMER_LOCATION_TYPES.footer];
  const aboveFooterDisclaimerUiState = uiState.syntheticContentEntities?.disclaimers?.[DISCLAIMER_LOCATION_TYPES.body];
  const belowFooterDisclaimerUiState = uiState.syntheticContentEntities?.disclaimers?.[DISCLAIMER_LOCATION_TYPES.footer];

  const { fetchFieldData } = useFetchSfFieldData();

  const validateAndChangeProgramOfStudy = (keyPath, value) => {
    const chosenPoS = availableProgramsOfStudy.find(((pos) => pos.id === +value)) || {};
    updateProgramOfStudy(chosenPoS);

    if (!Object.keys(chosenPoS).length) {
      fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_PROGRAM_OF_STUDY);
      return;
    }
    fieldValidityChanged(keyPath, true);
  };

  const validateAndChangeDegreeOffering = (keyPath, value) => {
    const chosenProgram = availablePrograms.find((program) => program.programId === +value) || {};

    updateDefaultDegreeOffering(chosenProgram);
    fetchFieldData(chosenProgram.salesforceOrg);

    if (!Object.keys(chosenProgram).length) {
      fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_DEGREE_OFFERING);
      return;
    }
    fieldValidityChanged(keyPath, true);
  };

  const validateAndChangeFormName = (keyPath, value) => {
    onFieldValueChange(keyPath, value);

    if (value.length === 0) {
      fieldValidityChanged(keyPath, false, ERROR_TEXT_EMPTY_FORM_NAME);
    } else {
      fieldValidityChanged(keyPath, true);
    }
  };

  const validateDisclaimers = ({ impliedConsent = isImpliedConsent, disclaimerType, disclaimerShow } = {}) => {
    const disclaimers = form.syntheticContentEntities?.disclaimers;
    if (!disclaimers) return;

    const hasAtLeastOneDisclaimer = Object.keys(disclaimers).some((key) => Boolean(key === disclaimerType ? disclaimerShow : disclaimers[key]?.show));

    Object.keys(disclaimers).forEach((key) => {
      const disclaimer = disclaimers[key];
      const show = key === disclaimerType ? disclaimerShow : disclaimer?.show;
      const value = disclaimer?.value;

      if (impliedConsent && !hasAtLeastOneDisclaimer) {
        fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'show'], false, ERROR_DISCLAIMER_REQUIRED);
        fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'value'], true);
        return;
      }

      if (show && !value?.length) {
        fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'show'], true);
        fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'value'], false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
        return;
      }

      fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'show'], true);
      fieldValidityChanged(['syntheticContentEntities', 'disclaimers', key, 'value'], true);
    });
  };

  const validateAndChangeGDPRSetting = (keyPath, value) => {
    const impliedConsent = !(LEGACY_GDPR_SETTINGS.includes(value));

    onConsentStyleChange(value);
    validateDisclaimers({ impliedConsent });

    if (value.length === 0) {
      fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_GDPR_SETTING);
    } else {
      fieldValidityChanged(keyPath, true);
    }
  };

  const validateAndChangeDisclaimerText = (keyPath, value) => {
    onFieldValueChange(keyPath, value);
    if (value.length === 0) {
      fieldValidityChanged(keyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
    } else {
      fieldValidityChanged(keyPath, true);
    }
  };

  const validateDisclaimerOverride = (disclaimerType, show) => {
    const overrideKeyPath = ['syntheticContentEntities', 'disclaimers', disclaimerType, 'override'];
    const overrideValue = form.syntheticContentEntities?.disclaimers?.[disclaimerType]?.override;

    if (!isGeoImplied || !show) {
      fieldValidityChanged(overrideKeyPath, true);
      return;
    }

    if (!overrideValue?.trim().length) {
      fieldValidityChanged(overrideKeyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
      return;
    }

    fieldValidityChanged(overrideKeyPath, true);
  };

  const validateAndChangeDisclaimerCheckbox = (disclaimerType) => (keyPath, value) => {
    onFieldValueChange(keyPath, value);
    validateDisclaimers({ disclaimerType, disclaimerShow: value });
    validateDisclaimerOverride(disclaimerType, value);
  };

  const programName = () => {
    if (supportsDegreeOffering) {
      return form.programId > -1 ? (
        <h6 className="small">{form.programFullName || `Program name not available for ${form.programOffering} from Central Park.`} </h6>
      ) : null;
    }
    return null;
  };

  const conditionalityWarning = () => {
    if (supportsDegreeOffering) {
      return Object.keys(form.fieldConditionalityInfo.branchingFields).length ? (
        <h6 className="small">Conditional degree offering(s) below could override default degree offering.</h6>
      ) : null;
    }
    return null;
  };

  // TODO - better way to lump the items that all render on hasDegreeOffering?
  const showDegreeOfferingOrProgramOfStudyDD = () => {
    if (supportsDegreeOffering) {
      return (
        <InputContainer
          label="Default Degree Offering"
          fieldType="tx-form"
          keyPath={['degreeOffering']}
          uiState={uiState.degreeOffering}
        >
          <SelectAttribute
            className="mb-2 "
            id="degree_offering_options"
            keyPath={['degreeOffering']}
            onFieldValueChange={validateAndChangeDegreeOffering}
            onFieldUIStateChange={onUIStateChange}
            options={buildAvailablePrograms(availablePrograms)}
            value={form.programId}
          />
        </InputContainer>
      );
    }

    const posID = form.programOfStudy?.id ? form.programOfStudy.id : '';
    return (
      <InputContainer
        label="Program Of Study"
        fieldType="tx-form"
        keyPath={['programOfStudy']}
        uiState={uiState.programOfStudy}
      >
        <SelectAttribute
          className="mb-2 "
          id="program_of_study_options"
          keyPath={['programOfStudy']}
          onFieldValueChange={validateAndChangeProgramOfStudy}
          onFieldUIStateChange={onUIStateChange}
          options={buildAvailableProgramsOfStudy(availableProgramsOfStudy)}
          sortOptions
          value={posID}
        />
      </InputContainer>
    );
  };

  const disclaimerLabel = isImpliedConsent ? '' : '(optional)';
  const gdprSettingOptions = GDPR_SETTING_DISPLAY_LABELS;

  return (
    <>
      <div className="row mt-3">
        <div className="col-4 ">
          <InputContainer
            label="Form Name"
            fieldType="tx-form"
            keyPath={['name']}
            uiState={uiState.name}
          >
            <TextAttribute
              keyPath={['name']}
              value={form.name || ''}
              className="mb-2 "
              placeholder="Form name..."
              onFieldValueChange={validateAndChangeFormName}
              onFieldUIStateChange={onUIStateChange}
            />
          </InputContainer>
          <h6 className="small font-italic">* Denotes field is required.</h6>
        </div>
        <div className="col-4">
          {showDegreeOfferingOrProgramOfStudyDD()}
          {programName()}
          {conditionalityWarning()}
        </div>
        <div className="col-4">
          <InputContainer
            label="GDPR Consent"
            fieldType="tx-form"
            keyPath={['syntheticContentEntities', 'gdprSetting']}
            uiState={uiState.syntheticContentEntities.gdprSetting}
          >
            <SelectAttribute
              className="mb-2 "
              id="gdpr_setting_options"
              keyPath={['syntheticContentEntities', 'gdprSetting']}
              onFieldValueChange={validateAndChangeGDPRSetting}
              options={gdprSettingOptions}
              value={form.syntheticContentEntities.gdprSetting}
              onFieldUIStateChange={onUIStateChange}
            />
          </InputContainer>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col">
          <div className="row mb-2">
            <div className="col">
              <CheckBoxAttribute
                className="mt-1"
                fieldType="tx-form"
                keyPath={['formStyle']}
                onCheckBoxChange={(keyPath, checked) => onFieldValueChange(keyPath, checked ? FORM_STYLE.QUIZ : FORM_STYLE.STANDARD)}
                value={form.formStyle === FORM_STYLE.QUIZ}
              >
                <span className="font-weight-bold">Use Quiz form style</span>
              </CheckBoxAttribute>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="row mb-2">
            <div className="col">
              <InputContainer
                keyPath={aboveFooterDisclaimerKeyPath.concat('show')}
                uiState={aboveFooterDisclaimerUiState?.show}
                fieldType="tx-form"
              >
                <CheckBoxAttribute
                  className="mt-1"
                  keyPath={aboveFooterDisclaimerKeyPath.concat('show')}
                  value={aboveFooterDisclaimer?.show}
                  onCheckBoxChange={validateAndChangeDisclaimerCheckbox(DISCLAIMER_LOCATION_TYPES.body)}
                >
                  <span className="font-weight-bold">Include disclaimer language above submit button {disclaimerLabel}</span>
                </CheckBoxAttribute>
              </InputContainer>
            </div>
          </div>
          {aboveFooterDisclaimer?.show ? (
            <>
              <div className="row mb-3">
                <div className="col">
                  <InputContainer
                    keyPath={aboveFooterDisclaimerKeyPath.concat('value')}
                    uiState={aboveFooterDisclaimerUiState?.value}
                    fieldType="tx-form"
                    label={isGeoImplied ? 'Default Disclaimer Language (US) *' : ''}
                  >
                    <TextAreaAttribute
                      keyPath={aboveFooterDisclaimerKeyPath.concat('value')}
                      value={aboveFooterDisclaimer?.value}
                      onFieldValueChange={validateAndChangeDisclaimerText}
                      onFieldUIStateChange={onUIStateChange}
                      placeholder="By consenting to receive communications, I am etc. etc."
                    />
                  </InputContainer>
                </div>
              </div>
              {isGeoImplied ? (
                <div className="row mb-3">
                  <div className="col">
                    <InputContainer
                      keyPath={aboveFooterDisclaimerKeyPath.concat('override')}
                      uiState={aboveFooterDisclaimerUiState?.override}
                      fieldType="tx-form"
                      label="GDPR Disclaimer Language"
                    >
                      <TextAreaAttribute
                        keyPath={aboveFooterDisclaimerKeyPath.concat('override')}
                        value={aboveFooterDisclaimer?.override}
                        onFieldValueChange={validateAndChangeDisclaimerText}
                        onFieldUIStateChange={onUIStateChange}
                        placeholder="This will override the form's disclaimer language in GDPR regions."
                      />
                    </InputContainer>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="row mb-2">
            <div className="col">
              <InputContainer
                keyPath={belowFooterDisclaimerKeyPath.concat('show')}
                uiState={belowFooterDisclaimerUiState?.show}
                fieldType="tx-form"
              >
                <CheckBoxAttribute
                  className="mt-1"
                  keyPath={belowFooterDisclaimerKeyPath.concat('show')}
                  value={belowFooterDisclaimer?.show}
                  onCheckBoxChange={validateAndChangeDisclaimerCheckbox(DISCLAIMER_LOCATION_TYPES.footer)}
                >
                  <span className="font-weight-bold">Include disclaimer language below submit button {disclaimerLabel}</span>
                </CheckBoxAttribute>
              </InputContainer>
            </div>
          </div>
          {belowFooterDisclaimer?.show ? (
            <>
              <div className="row mb-3">
                <div className="col">
                  <InputContainer
                    keyPath={belowFooterDisclaimerKeyPath.concat('value')}
                    uiState={belowFooterDisclaimerUiState?.value}
                    fieldType="tx-form"
                    label={isGeoImplied ? 'Default Disclaimer Language (US)' : ''}
                  >
                    <TextAreaAttribute
                      keyPath={belowFooterDisclaimerKeyPath.concat('value')}
                      value={belowFooterDisclaimer?.value}
                      onFieldValueChange={validateAndChangeDisclaimerText}
                      onFieldUIStateChange={onUIStateChange}
                      placeholder="By consenting to receive communications, I am etc. etc."
                    />
                  </InputContainer>
                </div>
              </div>
              {isGeoImplied ? (
                <div className="row mb-3">
                  <div className="col">
                    <InputContainer
                      keyPath={belowFooterDisclaimerKeyPath.concat('override')}
                      uiState={belowFooterDisclaimerUiState?.override}
                      fieldType="tx-form"
                      label="GDPR Disclaimer Language"
                    >
                      <TextAreaAttribute
                        keyPath={belowFooterDisclaimerKeyPath.concat('override')}
                        value={belowFooterDisclaimer?.override}
                        onFieldValueChange={validateAndChangeDisclaimerText}
                        onFieldUIStateChange={onUIStateChange}
                        placeholder="This will override the forms disclaimer language in GDPR regions."
                      />
                    </InputContainer>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

FormDetails.propTypes = {
  availablePrograms: PropTypes.array,
  availableProgramsOfStudy: PropTypes.array,
  availableCredentialTypes: PropTypes.array,
  fieldValidityChanged: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  onConsentStyleChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  onUIStateChange: PropTypes.func.isRequired,
  supportsDegreeOffering: PropTypes.bool.isRequired,
  uiState: PropTypes.object.isRequired,
  updateDefaultDegreeOffering: PropTypes.func.isRequired,
  updateProgramOfStudy: PropTypes.func.isRequired,
};

export default FormDetails;
